import { Deferred } from "@inertiajs/react";
import { format } from "date-fns";
import {
	Activity,
	Cake,
	Calendar,
	Check,
	ClipboardList,
	Copy,
	Dot,
	Globe,
	History,
	Link,
	Mail,
	MapPinIcon,
	Phone,
} from "lucide-react";
import { memo, Suspense, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { useDateContext } from "@/components/providers/date-provider";
import ApptCard from "@/components/shared/appt-card";
import { LoadingBasic } from "@/components/shared/loading";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getGenderIcon } from "@/hooks/use-gender";
import { getRegistrationSourceLabel } from "@/lib/patients";
import { cn, generateInitials } from "@/lib/utils";
import type { PatientIndexGlobalPageProps } from "@/pages/AdminPortal/Patient/Index";

export interface DetailsContentProps {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
	appts: PatientIndexGlobalPageProps["selectedPatientAppts"];
	open: boolean;
	onOpenChange?: (open: boolean) => void;
	onPageChange: (page: number, limit?: number) => void;
}

const DetailsContent = memo(function Component({
	patient,
	appts,
	open,
	onOpenChange,
	onPageChange,
}: DetailsContentProps) {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const { t: tpd } = useTranslation("patients", { keyPrefix: "details" });

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className="flex flex-col rounded-t-[10px] mt-24 max-h-[90%] fixed bottom-0 left-0 right-0 outline-none">
				<div className="flex-1 overflow-y-auto">
					<div className="w-full max-w-sm mx-auto md:max-w-5xl">
						<DrawerHeader>
							<DrawerTitle>{tpd("title")}</DrawerTitle>
							<DrawerDescription className="max-w-xs">
								{tpd("description")}
							</DrawerDescription>
						</DrawerHeader>

						<div className="p-4">
							<Tabs defaultValue="basic" className="w-full">
								<TabsList className="w-full justify-start">
									<TabsTrigger value="basic" className="w-full md:w-auto">
										Basic
									</TabsTrigger>
									<TabsTrigger
										value="appointments"
										className="w-full md:w-auto"
									>
										Appointments
									</TabsTrigger>
								</TabsList>

								<TabsContent
									value="basic"
									className="flex flex-col gap-4 max-h-[500px] overflow-y-auto mt-0"
								>
									<div className="grid grid-cols-12 gap-4 py-4">
										<ContactCard patient={patient} />

										<ProfileCard patient={patient} />

										<div className="grid grid-cols-1 lg:grid-cols-2 gap-4 col-span-full">
											<Card className="shadow-inner">
												<CardContent className="p-6">
													<Accordion
														type="single"
														collapsible
														defaultValue="addresses"
													>
														<AccordionItem
															value="addresses"
															className="border-0"
														>
															<AccordionTrigger className="p-0 uppercase">
																{tpl("addresses.label")}
															</AccordionTrigger>
															<AccordionContent className="flex flex-col gap-4 mt-4">
																<AddressCard patient={patient} />
															</AccordionContent>
														</AccordionItem>
													</Accordion>
												</CardContent>
											</Card>

											<Card className="shadow-inner">
												<CardContent className="p-6">
													<div className="flex flex-col gap-4">
														<p className="text-sm font-medium uppercase text-sm">
															Account Activity
														</p>
														<AccountActivityCard patient={patient} />
													</div>
												</CardContent>
											</Card>
										</div>
									</div>
								</TabsContent>

								<TabsContent
									value="appointments"
									className="gap-4 max-h-[500px] overflow-y-auto mt-0"
								>
									<Deferred
										data={["selectedPatientAppts"]}
										fallback={<LoadingBasic columnBased />}
									>
										<div className="p-2 md:p-4 py-4 space-y-6">
											{!appts?.data?.length ? (
												<p className="text-sm font-light text-center text-muted-foreground">
													{tpl("appointments.empty")}
												</p>
											) : (
												<>
													{appts.data.map((group) => (
														<div
															key={group.date}
															className="flex flex-col gap-2"
														>
															<div className="flex items-center gap-2 mb-1 opacity-75">
																<span className="text-sm font-bold text-foreground text-nowrap">
																	{group.date === "Unscheduled"
																		? group.date
																		: format(group.date, "PP")}
																</span>
																<hr className="w-[3rem] border-foreground/25" />
																<span className="text-xs text-muted-foreground italic text-nowrap">
																	{group.schedules.length} Total Visit's
																</span>
																<hr className="w-full border-foreground/25" />
															</div>

															<div className="grid grid-cols-1 lg:grid-cols-2">
																{group.schedules.map((appt) => (
																	<Suspense
																		key={appt.id}
																		fallback={
																			<LoadingBasic columnBased={true} />
																		}
																	>
																		<ApptCard appt={appt} hideDate />
																	</Suspense>
																))}
															</div>
														</div>
													))}

													{appts.metadata && (
														<ApptPagination
															metadata={appts.metadata}
															actions={{
																goToPrevpage: () =>
																	onPageChange(
																		appts.metadata.page - 1,
																		appts.metadata.limit,
																	),
																goToNextPage: () =>
																	onPageChange(
																		appts.metadata.page + 1,
																		appts.metadata.limit,
																	),
																goToPage: (value) =>
																	onPageChange(
																		Number(value),
																		appts.metadata.limit,
																	),
																goToFirstPage: () =>
																	onPageChange(1, appts.metadata.limit),
																goToLastPage: () =>
																	onPageChange(
																		appts.metadata.pages,
																		appts.metadata.limit,
																	),
																onChangeLimit: (value) =>
																	onPageChange(1, Number(value)),
															}}
														/>
													)}
												</>
											)}
										</div>
									</Deferred>
								</TabsContent>
							</Tabs>
						</div>

						<DrawerFooter>
							<DrawerClose asChild>
								<Button
									type="button"
									variant="outline"
									size="lg"
									className="shadow-none bg-card"
								>
									{td("modal.close")}
								</Button>
							</DrawerClose>
						</DrawerFooter>
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
});
export default DetailsContent;

const ProfileCard = memo(function Component({
	patient,
}: {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}) {
	const { locale, tzDate } = useDateContext();
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const initials = useMemo(
		() => generateInitials(patient.name),
		[patient.name],
	);

	return (
		<div
			title={tpl("profile")}
			className="flex flex-col gap-2 col-span-full md:col-span-6"
		>
			<p className="uppercase tracking-wider text-foreground opacity-50 text-xs font-bold">
				{tpl("profile")}
			</p>

			<Card className="h-full shadow-inner">
				<CardHeader>
					<div className="flex items-center w-full gap-2 text-left">
						<Avatar className="border rounded-lg size-8 bg-muted">
							<AvatarImage src="#" alt={patient?.name || "N/A"} />
							<AvatarFallback className="text-xs rounded-lg bg-muted">
								{initials}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 space-y-0.5 text-sm leading-tight text-left">
							<p className="font-bold uppercase truncate">
								{patient.name || "N/A"}
							</p>
						</div>
					</div>
				</CardHeader>

				<CardContent className="grid gap-4">
					<div className="flow-root">
						<dl className="-my-3 text-sm grid grid-cols-1">
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("gender.label")}
								</dt>

								<dd className="sm:col-span-2">
									<Badge variant="outline">
										<span className="flex items-center gap-1 text-xs uppercase font-medium">
											{getGenderIcon(patient.gender, "size-3.5")}
											{tpl(`gender.${patient.gender.toLowerCase()}`)}
										</span>
									</Badge>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">{tpl("age.label")}</dt>

								<dd className="sm:col-span-2">
									<Badge variant="outline">
										<p className="flex items-center gap-1 font-medium">
											<Cake className="size-3" />
											<span>
												{patient.age} {tpl("age.years")}
											</span>
										</p>
									</Badge>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">{tpl("dob")}</dt>

								<dd className="sm:col-span-2">
									<span className="font-medium">
										{format(new Date(String(patient.dateOfBirth)), "PP", {
											locale,
											in: tzDate,
										})}
									</span>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("verified.label")}
								</dt>

								<dd className="sm:col-span-2">
									<Badge
										variant="accent"
										className={cn(
											"px-1.5 py-1 rounded-full",
											patient.isVerified ? "bg-green-100" : "bg-red-100",
										)}
									>
										<span
											className={cn(
												"flex items-center gap-1 text-[10px] uppercase font-bold",
												patient.isVerified
													? "text-emerald-500"
													: "text-red-500",
											)}
										>
											{patient.isVerified
												? tpl("verified.verified")
												: tpl("verified.unverified")}
										</span>
									</Badge>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">Reg. Number</dt>

								<dd className="sm:col-span-2">
									<span className="font-mono text-xs font-bold">
										{patient.patientNumber || "N/A"}
									</span>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("registered_at")}
								</dt>

								<dd className="sm:col-span-2">
									<span className="font-medium">
										{format(new Date(String(patient.createdAt)), "PP", {
											locale,
											in: tzDate,
										})}
									</span>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("order_count")}
								</dt>

								<dd className="sm:col-span-2">
									<Badge variant="accent" className="px-1 rounded-full">
										<p className="flex items-center gap-1 font-medium">
											<ClipboardList className="size-3" />
											<span>{patient.orderCount || 0} Orders</span>
										</p>
									</Badge>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("appointment_count")}
								</dt>

								<dd className="sm:col-span-2">
									<Badge variant="accent" className="px-1 rounded-full">
										<p className="flex items-center gap-1 font-medium">
											<Calendar className="size-3" />
											<span>{patient.appointmentCount || 0} Visits</span>
										</p>
									</Badge>
								</dd>
							</div>
						</dl>
					</div>
				</CardContent>
			</Card>
		</div>
	);
});

const CopyButton = memo(function Component({ value }: { value?: string }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		if (!value || value === "N/A") return;
		navigator.clipboard.writeText(value);
		setCopied(true);
		toast.success("Copied to clipboard");
		setTimeout(() => setCopied(false), 2000);
	};

	if (!value || value === "N/A") return null;

	return (
		<Button
			variant="ghost"
			size="icon"
			className="size-6 ml-auto hover:bg-muted"
			onClick={handleCopy}
		>
			{copied ? (
				<Check className="size-3 text-emerald-500" />
			) : (
				<Copy className="size-3 opacity-50 group-hover:opacity-100 transition-opacity" />
			)}
		</Button>
	);
});

const ContactCard = memo(function Component({
	patient,
}: {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}) {
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const initialsContact = useMemo(() => {
		if (!patient.contact?.contactName) return "N/A";

		return generateInitials(patient.contact.contactName);
	}, [patient.contact?.contactName]);

	return (
		<div
			title={tpl("contact")}
			className="flex flex-col gap-2 col-span-full md:col-span-6"
		>
			<p className="uppercase tracking-wider text-foreground opacity-50 text-xs font-bold">
				{tpl("contact")}
			</p>

			<Card className="h-full shadow-inner">
				<CardHeader>
					<div className="flex items-center w-full gap-2 text-left">
						<Avatar className="border rounded-lg size-8 bg-muted">
							<AvatarImage
								src="#"
								alt={patient.contact?.contactName || "N/A"}
							/>
							<AvatarFallback className="text-xs rounded-lg bg-muted">
								{initialsContact}
							</AvatarFallback>
						</Avatar>
						<div className="flex-1 space-y-0.5 text-sm leading-tight text-left">
							<p className="font-bold uppercase truncate">
								{patient.contact?.contactName || "N/A"}
							</p>
						</div>
					</div>
				</CardHeader>

				<CardContent className="grid gap-4">
					<div className="flow-root">
						<dl className="-my-3 text-sm">
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									{tpl("contact_phone")}
								</dt>

								<dd className="sm:col-span-2">
									<div className="flex items-center justify-between group">
										<span className="flex items-center gap-1 font-medium">
											<Phone className="size-3.5 opacity-75" />
											{patient.contact?.contactPhone || "N/A"}
										</span>
										<CopyButton value={patient.contact?.contactPhone} />
									</div>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">Email</dt>

								<dd className="sm:col-span-2">
									<div className="flex items-center justify-between group">
										<span className="flex items-center gap-1 font-medium">
											<Mail className="size-3.5 opacity-75" />
											{patient.contact?.email || "N/A"}
										</span>
										{patient.contact?.email && (
											<CopyButton value={patient.contact?.email} />
										)}
									</div>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">MiiTel Link</dt>

								<dd className="sm:col-span-2">
									<div className="flex items-center justify-between group">
										<span className="flex items-center gap-1 font-medium">
											<Link className="size-3.5 opacity-75" />
											<span className="truncate max-w-[150px] sm:max-w-xs">
												{patient.contact?.miitelLink || "N/A"}
											</span>
										</span>
										{patient.contact?.miitelLink && (
											<CopyButton value={patient.contact?.miitelLink} />
										)}
									</div>
								</dd>
							</div>
							<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
								<dt className="text-muted-foreground/75">
									Registration Source
								</dt>

								<dd className="sm:col-span-2">
									<span className="flex items-center gap-1 font-medium">
										<Globe className="size-3.5 opacity-75" />
										{getRegistrationSourceLabel(patient.registrationSource)}
									</span>
								</dd>
							</div>
						</dl>
					</div>
				</CardContent>
			</Card>
		</div>
	);
});

const AddressCard = memo(function Component({
	patient,
}: {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}) {
	const { t: tpl } = useTranslation("patients", { keyPrefix: "list" });
	const addresses = useMemo(
		() => patient?.patientAddresses || [],
		[patient?.patientAddresses],
	);

	if (!addresses.length) {
		return (
			<p className="text-sm font-light text-center text-muted-foreground">
				{tpl("addresses.empty")}
			</p>
		);
	}

	return (
		<Accordion type="single" collapsible>
			{addresses.map((a) => (
				<AccordionItem key={a.id} value={String(a.id)}>
					<AccordionTrigger>
						<div className="flex items-center">
							<p className="flex-1 font-medium">
								{a.address.location.state} - {a.address.location.city}
							</p>
							{a.active && (
								<Dot
									className="ml-3 mr-1 shrink-0 text-emerald-600"
									width={10}
									height={10}
									strokeWidth={20}
								/>
							)}
						</div>
					</AccordionTrigger>

					<AccordionContent>
						<div className="flow-root col-span-full">
							<dl className="-my-3 text-sm ">
								<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
									<dt className="font-medium">{tpl("addresses.country")}</dt>

									<dd className="sm:col-span-2">
										<span>
											{a.address.location.countryCode} -{" "}
											{a.address.location.country}
										</span>
									</dd>
								</div>
								<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
									<dt className="font-medium">
										{tpl("addresses.state")} - {tpl("addresses.city")}
									</dt>

									<dd className="sm:col-span-2">
										<span>
											{a.address.location.state} - {a.address.location.city}
										</span>
									</dd>
								</div>
								<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
									<dt className="font-medium">{tpl("addresses.postal")}</dt>

									<dd className="sm:col-span-2">
										<span>{a.address.postalCode || "N/A"}</span>
									</dd>
								</div>
								<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
									<dt className="font-medium">
										{tpl("addresses.address_line")}
									</dt>

									<dd className="sm:col-span-2">
										<p className="text-pretty">{a.address.address}</p>
										<p className="italic">
											{tpl("addresses.notes")}: {a.address.notes || "N/A"}
										</p>
										<Button
											type="button"
											variant="primary-outline"
											size="sm"
											className="mt-2"
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();
												window.open(
													`https://www.google.com/maps/search/?api=1&query=${a.address?.coordinates[0]},${a.address?.coordinates[1]}`,
												);
											}}
										>
											<MapPinIcon />
											{tpl("addresses.view_on_google_maps")}
										</Button>
									</dd>
								</div>
							</dl>
						</div>
					</AccordionContent>
				</AccordionItem>
			))}
		</Accordion>
	);
});

const AccountActivityCard = memo(function Component({
	patient,
}: {
	patient: NonNullable<PatientIndexGlobalPageProps["selectedPatient"]>;
}) {
	const { locale, tzDate } = useDateContext();

	if (!patient.userId) {
		return (
			<p className="text-sm font-light text-center text-muted-foreground italic">
				No user account associated with this patient.
			</p>
		);
	}

	return (
		<div className="flow-root col-span-full">
			<dl className="-my-3 text-sm grid grid-cols-1">
				<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
					<dt className="font-medium">Current Sign In</dt>
					<dd className="sm:col-span-2">
						<div className="flex flex-col">
							<span className="flex items-center gap-1">
								<Activity className="size-3.5 opacity-75" />
								{patient.currentSignInAt
									? format(new Date(patient.currentSignInAt), "PPpp", {
											locale,
											in: tzDate,
										})
									: "Never"}
							</span>
							{patient.currentSignInIp && (
								<span className="text-xs text-muted-foreground ml-4.5">
									IP: {patient.currentSignInIp}
								</span>
							)}
						</div>
					</dd>
				</div>
				<div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
					<dt className="font-medium">Last Sign In</dt>
					<dd className="sm:col-span-2">
						<div className="flex flex-col">
							<span className="flex items-center gap-1">
								<History className="size-3.5 opacity-75" />
								{patient.lastSignInAt
									? format(new Date(patient.lastSignInAt), "PPpp", {
											locale,
											in: tzDate,
										})
									: "Never"}
							</span>
							{patient.lastSignInIp && (
								<span className="text-xs text-muted-foreground ml-4.5">
									IP: {patient.lastSignInIp}
								</span>
							)}
						</div>
					</dd>
				</div>
			</dl>
		</div>
	);
});
